import React, { useEffect, useMemo, useState } from 'react'

import GenericModal from '../../../components/component-items/modal'
import GlobalStyles from '../../../components/component-items/styles'

import { FulfillmentItemUpdate, SplitFulfillmentOrder, SplitOrderItem } from './types'

export type SplitOrdersEditCanonicalModalProps = {
  show: boolean
  onClose: () => void
  canonicalOrder?: SplitFulfillmentOrder
  onSave?: (items: FulfillmentItemUpdate[]) => Promise<void>
}

type EditableItem = SplitOrderItem & { __index: number }

const createEditableItems = (order?: SplitFulfillmentOrder): EditableItem[] =>
  order?.items?.map((item, index) => ({ ...item, __index: index })) || []

export const SplitOrdersEditCanonicalModal = ({
  show,
  onClose,
  canonicalOrder,
  onSave,
}: SplitOrdersEditCanonicalModalProps) => {
  const [items, setItems] = useState<EditableItem[]>(createEditableItems(canonicalOrder))
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    setItems(createEditableItems(canonicalOrder))
  }, [canonicalOrder])

  const modalTitle = useMemo(() => {
    if (!canonicalOrder) {
      return 'Edit Canonical Order Items'
    }

    return `Edit Items • Order #${canonicalOrder.id}`
  }, [canonicalOrder])

  const handleQuantityChange = (editableIndex: number, value: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.__index === editableIndex
          ? {
              ...item,
              quantity: Number.isNaN(value) ? item.quantity : value,
              allocatedQuantity: Number.isNaN(value) ? item.allocatedQuantity : value,
            }
          : item
      )
    )
  }

  const handleDispositionChange = (editableIndex: number, value: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.__index === editableIndex
          ? {
              ...item,
              disposition: value as SplitOrderItem['disposition'],
            }
          : item
      )
    )
  }

  const handleSave = async () => {
    if (!onSave) {
      onClose()
      return
    }

    setIsSaving(true)
    setErrorMessage(null)

    const payload: FulfillmentItemUpdate[] = items
      .filter((item) => item.groupLineId !== undefined && item.groupLineId !== null)
      .map((item) => ({
        groupLineId: item.groupLineId as string | number,
        quantity: item.allocatedQuantity ?? item.quantity ?? 0,
        disposition: (item.disposition as FulfillmentItemUpdate['disposition']) || 'SHIP',
      }))

    try {
      await onSave(payload)
      onClose()
    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : 'Unable to save canonical items.'
      setErrorMessage(message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <GenericModal
      heading={modalTitle}
      show={show}
      onHide={onClose}
      buttons={
        <GlobalStyles.Button type="button" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save Changes'}
        </GlobalStyles.Button>
      }
    >
      {errorMessage ? (
        <p style={{ color: '#f64e60', marginBottom: '0.75rem' }} role="alert">
          {errorMessage}
        </p>
      ) : null}
      {items.length ? (
        <GlobalStyles.DataTable>
          <thead>
            <tr>
              <th>
                <span className="text">Item</span>
              </th>
              <th>
                <span className="text">SKU</span>
              </th>
              <th>
                <span className="text">Quantity</span>
              </th>
              <th>
                <span className="text">Disposition</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.__index}>
                <td>
                  <span className="text">{item.name || 'Unnamed Variant'}</span>
                </td>
                <td>
                  <span className="text">{item.sku || '—'}</span>
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    value={item.allocatedQuantity ?? item.quantity ?? ''}
                    onChange={(event) => {
                      const { value } = event.target
                      const previousQuantity = item.allocatedQuantity ?? item.quantity ?? 0
                      handleQuantityChange(item.__index, value === '' ? previousQuantity : Number(value))
                    }}
                    style={{ width: '5rem' }}
                  />
                </td>
                <td>
                  <select
                    value={item.disposition || 'SHIP'}
                    onChange={(event) => handleDispositionChange(item.__index, event.target.value)}
                  >
                    <option value="SHIP">Ship</option>
                    <option value="BACKORDER">Backorder</option>
                    <option value="CANCEL">Cancel</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </GlobalStyles.DataTable>
      ) : (
        <p style={{ padding: '1.5rem 0', color: '#6c7293' }}>No items are currently assigned to the canonical order.</p>
      )}
    </GenericModal>
  )
}

export default SplitOrdersEditCanonicalModal
